
import { Component, Vue, Prop, Watch } from 'vue-property-decorator'
import { commonModule, installationsModule, jobsModule, actionTypesModule, expensesModule, companyModule, itemsModule } from '@/store/modules/store-accessor'
import { TimeUtil } from '@/helpers/TimeUtil'
import Dropdown from '../../../components/dropdown/Dropdown.vue'
import DropdownItem from '../../../components/dropdown/DropdownItem.vue'
import { formatAmount, getOptimalTextColor, entryIcon, templatesFilenameLanguage, sortTableDates, pagesExportLimit } from "@/helpers/data";
import config from '@/config'
import { install } from 'vue2-google-maps'
import FileSaver from 'file-saver';
import dayjs from 'dayjs'
import SignatureFields from '@/components/Sideview/SignatureFields.vue';
import ExportMenu from "@/components/ExportMenu/ExportMenu.vue";

@Component({
  components: { Dropdown, DropdownItem, SignatureFields, ExportMenu }
})
export default class InstallationsSideviewJobsTab extends Vue {
  @Prop() installation: Record<string, any>;
  @Prop() jobId: number;

  expansionJobIdIndex = 0;
  isExpanded = false;

  isDownloadingCompleteJobPdf = false;
  isDownloadingExport = false;

  tableheaders: any [] = [
    { class:'font-weight-black', text: this.$t("actions"), sortable: false, value: 'actions'},
    { class:'font-weight-black', text: this.$t("item"), sortable: false, value: 'items' },
    { class:'font-weight-black', text: this.$t("last_job_entry"), sortable: true, value: 'last_job_entry_at', sort: (a: string, b: string) => sortTableDates(a, b), width: '250px' },
    { class:'font-weight-black', text: this.$t("cost"), sortable: true, value: 'cost' , width: '100px', align: 'center'},
    { class:'font-weight-black', text: this.$t("balance"), sortable: true, value: 'balance', width: '105px', align: 'center' },
    { class:'', text: '', value: 'is_complete', width: '85px', sortable: false, align: 'right' }, // Move expand arrow to the right
    { class:'', text: '', value: 'data-table-expand' }, // Move expand arrow to the right
  ]

  expandedItems: any[] = [];

  tableOptions = {itemsPerPage: 10, page: 1};

   mounted() {
    commonModule.getPersonnelCombo();
    commonModule.getActionCombo();
    commonModule.getDurationCombo();
    actionTypesModule.getActionTypesCombo();
    commonModule.getPersonnelPartnersCombo();
    if(this.installation?.info?.id) {
      itemsModule.getInstallationPrimaryInventoryItems(this.installation.info.id);
    }
  }

   updated() {
    if (this.jobs.length > 0 && this.jobId && !this.isExpanded) {
      this.showExpansion();
      this.isExpanded = true;
    }
  }

  showExpansion() {
    let expansionJob = installationsModule.mapJobsAndActions.find(i => i.id == this.jobId); // This is needed to find the index in the expansion panel
    const jobIndex = installationsModule.mapJobsAndActions.findIndex((a: any) => a.id == this.jobId);

    const page = Math.floor(jobIndex / this.tableOptions.itemsPerPage) + 1;

    this.tableOptions.page = page;
    this.expandedItems = [expansionJob];
  }

  @Watch("installation.info.id")
  updateJobs() {
    commonModule.getPersonnelCombo();
    commonModule.getActionCombo();
    commonModule.getDurationCombo();
    installationsModule.getInstallationsAction(installationsModule.filters);
    if (this.installation?.info?.id) {
      itemsModule.getInstallationPrimaryInventoryItems(this.installation.info.id);
    }
  }

  formatDate (date: Date): string {
    return TimeUtil.formatDateTime(date)
  }

  get jobs () {
    return installationsModule.mapJobsAndActions
  }

  get assigneeLabel() {
    if(companyModule.company?.modules?.includes('DFB_PORTAL_PARTNERS')) {
      return this.$i18n.t('userPartner');
    }
    else {
      return this.$i18n.t('user');
    }
  }

  calcCost(job: any, entryType: string) {
    if(entryType === 'payment') {
      return (job?.filter((item: any) => item.entry_type == entryType)).reduce((accumulator: number, item: any) => {return accumulator + parseFloat(item.amount)}, 0);
    }
    else {
      return (job?.filter((item: any) => item.entry_type == entryType)).reduce((accumulator: number, item: any) => {return accumulator + parseFloat(item.cost)}, 0);
    }
  }

  costColor(action: any) {
    switch (action.entry_type) {
      case 'payment':
        return 'darkGreen'
      case 'expense':
        return 'expenseChipColor'
      case 'action':
        return 'primary'
      case 'material':
        return 'primary'
      default:
        return '';
    }
  }

  calcProfit(job: Record<string, any>) {
    const actions = (job?.filter((item: any) => item.entry_type == 'action')).reduce((accumulator: number, item: any) => {return accumulator + parseFloat(item.cost)}, 0);
    const expenses = (job?.filter((item: any) => item.entry_type == 'expense')).reduce((accumulator: number, item: any) => {return accumulator + parseFloat(item.cost)}, 0);
    const materials = (job?.filter((item: any) => item.entry_type == 'material')).reduce((accumulator: number, item: any) => {return accumulator + parseFloat(item.cost)}, 0);
    return parseFloat((((actions + materials) - expenses)).toFixed(2));
  }

  costTextColor(action: any) {
    switch (action.entry_type) {
      case 'payment':
        return 'darkGreen'
      case 'expense':
        return 'expenseChipColor'
      case 'action':
        return 'primary'
      case 'material':
        return 'primary'
      default:
        return '';
    }
  }

  getEntryIcon(action: any){
    return entryIcon(action)
  }

  entryColor(action: any) {
    switch (action.entry_type) {
      case 'action':
        return action.type_color
      case 'payment':
        return 'darkGreen'
      case 'material':
        return 'materialChipColor'
      case 'expense':
        return 'expenseChipColor'
      default:
        return 'orange';
    }
  }

  entryTextColor(action: any) {
    switch (action.entry_type) {
      case 'action':
        return getOptimalTextColor(action.type_color)
      case 'payment':
        return 'darkGreen'
      case 'expense':
        return 'expenseChipColor'
      default:
        return 'black';
    }
  }

  entryName(action: any) {
    switch (action.entry_type) {
      case 'action':
        return `${action.type_name} ${action.unitInfo}`;
      case 'payment':
        return this.$i18n.t(action.entry_type)
      case 'material':
        if(action.description && action.unitInfo) {
          return `${action.description} ${action.unitInfo}`;
        } else {
          return (action.description || this.$i18n.t(action.entry_type));
        }
      case 'expense':
        return this.$i18n.t(action.entry_type)
      default:
        return this.$i18n.t(action.entry_type);
    }
  }

  jobTypeNameJoin(job: any) {
    return job.actions.map((x: any) => x.type_name)?.filter(Boolean).reverse().join(', ');
  }

  formatBalance(balance: number) {
    return formatAmount(balance);
  }

  get jobsExist() {
    return this.jobs.length > 0
  }

  async downloadExpenseFile(expense: Record<string, any>) {
    let downloadFile = await expensesModule.getExpensionFile(expense.id);
    downloadFile = new File([downloadFile], expense.file_name);
    FileSaver.saveAs(downloadFile);
  }

  canDeleteAction(action: any, job: any) {
    if(action.entry_type === 'action') {
      if(job?.actions?.filter((a: Record<string, any>) => a.entry_type === 'action').length === 1) {
        return false;
      }
    }

    if(job.cost_disabled)
        return ((job.cost - action.cost) - job.amount_paid >= 0)
    else
      return true
  }

  getDeleteActionTypeTooltipMsg(action: Record<string, any>, job: Record<string, any>) {
    if(job?.actions?.filter((action: Record<string, any>) => action.entry_type === 'action').length === 1) {
      return 'lastActionCannotDelete';
    }

    if(job.cost_disabled && (job.cost - action.cost - job.amount_paid) < 0) {
      return 'action_cannot_delete';
    }
  }

  addJob(){
    commonModule.showModal({ name: 'add-job-modal', payload: { installation: this.installation}})
  }

  bulkPay() {
    commonModule.showModal({
      name: 'quick-add-payments-modal',
      payload: {
        scope: 'installation',
        installationId: this.installation.info.id,
        entity: this.installation.info,
      },
    });
  }

  editJob (item: any){
    commonModule.showModal({ name: 'edit-job-modal', payload: { job: item}})
  }

  deleteJob (item: any){
    commonModule.showModal({ name: 'delete-job-modal', payload: { job: item}})
  }

  addAppointmentToJob (item: any){
    commonModule.showModal({ name: 'add-appointment-to-job-modal', payload: { job: item, installation: this.installation}})
  }

  addActionsToJob (item: any){
    commonModule.showModal({ name: 'add-actions-to-job-modal', payload: { job: item}})
  }

  addMaterialsToJob (item: any){
    commonModule.showModal({ name: 'add-materials-to-job-modal', payload: { job: item}})
  }

  addExpensesToJob (item: any){
    commonModule.showModal({ name: 'add-expense-to-job-modal', payload: { job: item}})
  }

  async addPaymentToJob (item: any){
    commonModule.showModal({ name: 'add-payment-to-job-modal', payload: { job: item, installation: this.installation}})
  }

  deletePayment (payment: any, job: any){
    commonModule.showModal({ name: 'delete-payment-modal', payload: { payment: payment, job: job}})
  }

  deleteAction (action: any, job: any){
    commonModule.showModal({ name: 'delete-action-modal', payload: { action: action, job: job}})
  }

  deleteMaterial (action: any, job: any){
    commonModule.showModal({ name: 'delete-material-modal', payload: { material: action, job: job}})
  }

  deleteExpense (action: any, job: any){
    commonModule.showModal({ name: 'delete-expense-modal', payload: { expense: action, job: job}})
  }

  editPayment (payment: any, job: any){
    if (this.isCreditPayment(payment)) return;
    commonModule.showModal({ name: 'edit-payment-modal', payload: { payment: payment, job: job, installation: this.installation}})
  }

  isCreditPayment(payment: any): boolean {
    return payment.source === "CREDIT" || payment.credit_ledger_id != null || payment.payment_type_id == null;
  }

  editAction (action: any, job: any){
    commonModule.showModal({ name: 'edit-action-modal', payload: { action: action, job: job}})
  }

  editMaterial (action: any, job: any){
    commonModule.showModal({ name: 'edit-material-modal', payload: { material: action, job: job}})
  }

  editExpense (action: any, job: any){
    commonModule.showModal({ name: 'edit-expense-modal', payload: { expense: action, job: job}})
  }

  addPhoto (item: any){
    commonModule.showModal({ name: 'add-photo-to-installation-modal', payload: { installation: item, tab: 'jobs' }})
  }

  showPhotos(job: any) {
    commonModule.showModal({ name: 'job-photos-modal', payload: { job: job}})
  }

  completeJob(job: JobDto) {
    commonModule.showModal({ name: 'complete-job-confirm-modal', payload: { job: job }});
  }

  downloadExportLengthCheck(type: string) {
    if (type === 'pdf') {
      return this.jobs?.length > 0 && this.jobs?.length <= pagesExportLimit();
    }
    return this.jobs?.length > 0;
  }

  async donwloadJobsExport(format: string) {
    this.isDownloadingExport = true;
    const data = {
      user_timezone: dayjs.tz.guess(),
      language: localStorage.getItem('lang'),
      format,
      installation_id: this.installation.info.id,
    };

    try {
      const company = await companyModule.getCompanyInfo();
      const filetype = format === 'excel' ? 'xlsx' : 'pdf';
      const filename = `${templatesFilenameLanguage()}_${company.entity_name}_DfB_${data.installation_id}_jobs_${dayjs().format('YYYY_MM_DD_HH_mm_ss')}.${filetype}`.replace(/ /g, '_');
      const exportFile = await installationsModule.getInstallationSideviewJobsExport(data);
      const file = new File([exportFile], filename);
      FileSaver.saveAs(file);
    } finally {
      this.isDownloadingExport = false;
    }
  }

  async donwloadCompletedJobPdf(job: Record<string, unknown>) {
    this.isDownloadingCompleteJobPdf = true;
    const userTimezone = dayjs.tz.guess();

    const data = { 'jobId': job.id, 'user_timezone': userTimezone, language: localStorage.getItem('lang') };

    try {
      if(typeof job.id === 'string') {
        const company = await companyModule.getCompanyInfo();
        const filename = `${templatesFilenameLanguage()}_${company.entity_name}_${this.installation.info.tag_id}_job_${job.id}.pdf`.replace(/ /g, '_');
        const pdf = await  jobsModule.getCompletedJobPdfExportAction(data);
        const file = new File([pdf], filename);
        FileSaver.saveAs(file);
      }
    } finally {
      this.isDownloadingCompleteJobPdf = false;
    }
  }

  get jobDescriptionIsEnabled() {
    return (companyModule.company?.preferences as any)?.job_description;
  }

  get tableIsLoading() {
    return installationsModule.sideviewTableIsLoading;
  }

  expansionText(job: Record<string, any>) {
    if(this.jobDescriptionIsEnabled && job.description?.length > 0) {
      return job.description;
    }
    return this.jobTypeNameJoin(job);
  }

  itemText(item: Record<string, any>) {
    const installationItemDescriptionIsEnabled = (companyModule.company?.preferences as any)?.installation_item_description;

    if(installationItemDescriptionIsEnabled) {
      const itemDescription = item.item_description && item.item_description.length > 0 ? item.item_description : '';
      return item.job_item && item.job_item.length > 0 ? `${itemDescription}${itemDescription ? ' | ' : ''}${item.job_item}` : '-'

    }

    return  item.job_item && item.job_item.length > 0 ? item.job_item : '-'
  }

  optimalTextColor(color: string) {
    return getOptimalTextColor(color);
  }

};
